import React, { ReactNode, createContext, useCallback, useState } from 'react';

import NotificationContainer from '../components/notifications/NotificationContainer';
import { Notification, NotificationAction } from '../types';

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (
    message: string,
    type: 'success' | 'error' | 'info',
    action?: NotificationAction,
  ) => void;
  removeNotification: (id: number) => void;
}

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = useCallback((id: number) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const addNotification = useCallback(
    (message: string, type: 'success' | 'error' | 'info', action?: NotificationAction) => {
      const id = Date.now();
      setNotifications((prev) => [
        ...prev,
        {
          id,
          message,
          title: type.charAt(0).toUpperCase() + type.slice(1),
          notification_type: 'in_app',
          severity: type,
          category: 'system',
          is_read: false,
          is_sent: true,
          user_id: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          action,
        },
      ]);
      setTimeout(() => {
        removeNotification(id);
      }, 5000); // Auto-remove after 5 seconds
    },
    [removeNotification],
  );

  return (
    <NotificationContext.Provider value={{ notifications, addNotification, removeNotification }}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
};
